<template>
  <q-card class="widget-card">
    <div class="widget-card__header">
      <div class="flex items-center no-wrap">
        <q-icon
          name="sym_r_travel_explore"
          size="22px"
          class="q-mr-xs"
          style="margin-top: -2px;"
        />
        <div class="widget-card__title">Local Market Comparison</div>
      </div>
      <div class="widget-card__subtitle">
        Compare your region to the nationwide market.
      </div>
    </div>
    <div class="q-px-md q-pb-sm">
      <q-select
        ref="localMarketConditionSelect"
        v-model="selectedCondition"
        :options="conditionOptions"
        color="secondary"
        dense
        @update:model-value="updateMetrics"
      />
    </div>
    <div class="widget-card__content">
      <div v-if="loading" class="q-pb-md q-pt-sm">
        <q-skeleton type="rect" height="40px" class="q-mb-sm" />
        <q-skeleton type="rect" height="40px" class="q-mb-sm" />
      </div>
      <div v-else class="q-gutter-y-xs">
        <div class="trend-item">
          <div class="trend-item__icon">
            <q-icon name="sym_r_attach_money" size="25px" />
          </div>
          <div class="trend-item__content">
            <div class="trend-item__label">Average Price</div>
            <div class="trend-item__value">{{ localAvgPrice }}</div>
          </div>
          <HighlightBadge
            :color="priceDiff < 0 ? 'positive' : 'negative'"
            :icon="priceDiff >= 0 ? 'sym_r_trending_up' : 'sym_r_trending_down'"
          >
            {{ Math.abs(priceDiff) }}%
            <span class="text-weight-regular">vs nationwide</span>
          </HighlightBadge>
        </div>
        <div class="trend-item">
          <div class="trend-item__icon">
            <q-icon name="sym_r_speed" size="23px" />
          </div>
          <div class="trend-item__content">
            <div class="trend-item__label">Average Mileage</div>
            <div class="trend-item__value">{{ localAvgMileage }} mi</div>
          </div>
          <HighlightBadge
            :color="mileageDiff < 0 ? 'positive' : 'negative'"
            :icon="
              mileageDiff >= 0 ? 'sym_r_trending_up' : 'sym_r_trending_down'
            "
          >
            {{ Math.abs(mileageDiff) }}%
            <span class="text-weight-regular">vs nationwide</span>
          </HighlightBadge>
        </div>
      </div>

      <!-- Market Comparison Insights -->
      <div v-if="!loading" class="insight-box q-mt-sm">
        <q-icon
          name="sym_r_campaign"
          size="20px"
          color="primary"
          class="q-mr-xs"
        />
        <div v-html="marketInsightText" class="insight-box__text" />
      </div>
    </div>
  </q-card>
</template>

<script>
import axios from "axios";
import mixins from "@/mixins";
import HighlightBadge from "@/components/UI/HighlightBadge.vue";

export default {
  name: "LocalMarketPulseWidget",
  components: {
    HighlightBadge
  },
  mixins: [mixins],
  data() {
    return {
      loading: true,
      localAvgPrice: "$0",
      localAvgMileage: "0",
      nationwideAvgPrice: 0,
      nationwideAvgMileage: 0,
      priceDiff: 0,
      mileageDiff: 0,
      selectedCondition: "All Vehicles",
      conditionOptions: ["All Vehicles", "New Vehicles", "Used Vehicles"]
    };
  },
  computed: {
    locationPostalCode() {
      return this.$store.state.search.location.postalCode || "";
    },
    searchRadius() {
      return this.$store.state.search.location.radius || 150;
    },
    marketInsightText() {
      const priceDiffAbs = Math.abs(this.priceDiff);
      const mileageDiffAbs = Math.abs(this.mileageDiff);
      // const isAllVehicles = this.selectedCondition === "All Vehicles";

      // Price-focused insights
      if (this.priceDiff < -8) {
        // const inventoryNote = isAllVehicles ? ' (may also reflect a higher proportion of used inventory being in the region)' : '';
        const inventoryNote = "";
        return `Vehicles are priced <b>${priceDiffAbs}% below</b> the national average${inventoryNote}—perfect for value-conscious buyers.`;
      } else if (this.priceDiff > 8) {
        // const inventoryNote = isAllVehicles ? ' (may also reflect a higher proportion of new inventory being in the region)' : '';
        const inventoryNote = "";
        return `Commands premium pricing at <b>${priceDiffAbs}% above</b> national averages${inventoryNote}.`;
      }

      // Moderate price differences
      if (this.priceDiff < -4) {
        // const inventoryNote = isAllVehicles ? ' (may also reflect a higher proportion of used inventory being in the region)' : '';
        const inventoryNote = "";
        return `Slightly more affordable, priced <b>${priceDiffAbs}% below</b> national averages${inventoryNote}.`;
      } else if (this.priceDiff > 4) {
        // const inventoryNote = isAllVehicles ? ' (may also reflect a higher proportion of new inventory being in the region)' : '';
        const inventoryNote = "";
        return `Slightly elevated pricing at <b>${priceDiffAbs}% above</b> national norms${inventoryNote}.`;
      }

      // Mileage-focused insights when price is neutral
      if (this.selectedCondition !== "New Vehicles") {
        if (this.mileageDiff < -10) {
          return `Exceptionally low-mileage vehicles—averaging <b>${mileageDiffAbs}% less</b> than the national market.`;
        } else if (this.mileageDiff > 10) {
          return `Averaging <b>${mileageDiffAbs}% above</b> the national mileage norm—giving potential value opportunities for buyers.`;
        }
      }

      // Balanced market
      return `Mirrors national trends, offering competitive pricing and typical mileage.`;
    }
  },
  mounted() {
    this.fetchMarketData();
  },
  methods: {
    async fetchMarketData() {
      try {
        const conditionValue = this.getConditionValue(this.selectedCondition);

        // Build filters array
        const filters = [];
        if (conditionValue !== null) {
          filters.push({
            condition: {
              equals: conditionValue
            }
          });
        }

        // Fetch local market data (within radius of postal code)
        const localPromise = axios.post(
          "https://api-v2.jumpfeed.com/network/publisher/products/vehicles",
          {
            filters,
            pagination: { page: 1, per_page: 1 },
            options: {
              location_search: {
                postal_code: this.locationPostalCode,
                radius: this.searchRadius,
                include_distance_field: false
              },
              faceting: {
                fields: {
                  price: ["range"],
                  mileage: ["range"]
                },
                size: 0
              }
            }
          }
        );

        // Fetch nationwide market data using the same endpoint as MarketPulseWidget
        const nationwidePromise = axios.get(
          "https://api-v2.jumpfeed.com/network/publisher/products/vehicles/nationwide-market-stats"
        );

        const [localResponse, nationwideResponse] = await Promise.all([
          localPromise,
          nationwidePromise
        ]);

        // Extract local averages from facets
        const localFacets = localResponse.data.metadata?.facets || {};
        const localPriceFacet = localFacets.price || [];
        const localMileageFacet = localFacets.mileage || [];

        // Get avg from range facets
        const localPriceAvg =
          localPriceFacet.find(f => f.key === "avg")?.doc_count || 0;
        const localMileageAvg =
          localMileageFacet.find(f => f.key === "avg")?.doc_count || 0;

        // Extract nationwide averages from the market-stats endpoint
        const nationwideData = nationwideResponse.data.data;
        const nationwideStats = nationwideData.find(
          s => s.condition === conditionValue
        );

        if (nationwideStats) {
          this.nationwideAvgPrice = parseFloat(nationwideStats.avg_price);
          this.nationwideAvgMileage = parseFloat(nationwideStats.avg_mileage);
        }

        // Set display values
        this.localAvgPrice = "$" + this.formatNumber(Math.round(localPriceAvg));
        this.localAvgMileage = this.formatNumber(
          Math.round(localMileageAvg),
          true,
          0
        );

        // Calculate percentage differences (local vs nationwide)
        if (this.nationwideAvgPrice > 0) {
          this.priceDiff = (
            ((localPriceAvg - this.nationwideAvgPrice) /
              this.nationwideAvgPrice) *
            100
          ).toFixed(1);
        } else {
          this.priceDiff = 0;
        }

        if (this.nationwideAvgMileage > 0) {
          this.mileageDiff = (
            ((localMileageAvg - this.nationwideAvgMileage) /
              this.nationwideAvgMileage) *
            100
          ).toFixed(1);
        } else {
          this.mileageDiff = 0;
        }
      } catch (error) {
        console.error("Failed to fetch local market data:", error);
      } finally {
        this.loading = false;
      }
    },
    getConditionValue(conditionLabel) {
      const conditionMap = {
        "All Vehicles": null,
        "New Vehicles": "New",
        "Used Vehicles": "Used"
      };
      return conditionMap[conditionLabel];
    },
    updateMetrics() {
      this.loading = true;
      this.fetchMarketData();
    }
  }
};
</script>

<style scoped lang="scss">
.trend-item {
  display: flex;
  align-items: center;
  gap: $space-sm;
  padding: $space-sm 0;
  background: color(background, lighter);
  border-radius: 6px;
  transition: all 0.2s ease;

  &__icon {
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: color(background, very-light);
    border-radius: $border-radius;
    margin-right: $space-xs;
  }

  &__content {
    flex: 1;
  }

  &__label {
    font-size: 0.8rem;
    color: color(text, light);
    font-weight: 600;
    margin-top: -2px;
  }

  &__value {
    font-size: 1.2rem;
    font-weight: 700;
    color: color(text);
    line-height: 1.2;
  }
}
</style>
